import type { ProductCard } from "@itaas/pep-contracts";
import { TECH_SUPPORT_PHONE } from "../config";

/** Modules dont l'essai implique un lecteur biométrique (livraison physique). */
export const BIOMETRIC_MODULE_SLUGS = new Set(["rh", "edu", "biometric-gateway"]);

export const DEFAULT_BIOMETRIC_TERMINALS = 1;

export function needsBiometricHardware(productSlugs: string[]): boolean {
  return productSlugs.some((s) => BIOMETRIC_MODULE_SLUGS.has(s));
}

export function isEducationTrial(industrySlug: string | null, productSlugs: string[]): boolean {
  return industrySlug === "education" || productSlugs.includes("edu");
}

export function isHealthcareTrial(industrySlug: string | null, productSlugs: string[]): boolean {
  return (
    industrySlug === "healthcare" ||
    industrySlug === "vertical-healthcare" ||
    productSlugs.includes("hospitol")
  );
}

export function filterTrialEligibleProducts(products: ProductCard[]): ProductCard[] {
  return products.filter((p) => p.trialDays && p.status !== "planned");
}

export function industryTrialUrl(industrySlug: string, productSlugs: string[]): string {
  const eligible = productSlugs.filter(Boolean);
  const qs = new URLSearchParams({ industry: industrySlug });
  if (eligible.length) qs.set("product", eligible[0]);
  return `/configure?${qs.toString()}`;
}

export function buildBiometricConfirmationMessage(opts: {
  industrySlug: string | null;
  productSlugs: string[];
  terminalCount: number;
  classroomCount?: number;
}): string {
  const { industrySlug, productSlugs, terminalCount, classroomCount } = opts;
  const phone = TECH_SUPPORT_PHONE;
  const lines: string[] = [];

  if (isEducationTrial(industrySlug, productSlugs)) {
    lines.push(
      "Votre demande de lecteur(s) biométrique a été transmise à l'équipe technique ITaaS.",
      `Quantité prévue : ${terminalCount} lecteur${terminalCount > 1 ? "s" : ""} (par défaut 1).`,
    );
    if (classroomCount && classroomCount > 0) {
      lines.push(`Nombre de salles de classe indiqué : ${classroomCount}.`);
    }
    lines.push(
      "Nous recommandons un lecteur par salle de classe pour un suivi optimal des présences.",
    );
  } else if (productSlugs.includes("rh")) {
    lines.push(
      "ITaaS RH s'accompagne d'un lecteur biométrique ZKTeco par défaut ; des lecteurs supplémentaires peuvent être ajoutés.",
      `Quantité demandée pour cet essai : ${terminalCount} lecteur${terminalCount > 1 ? "s" : ""}.`,
      "Votre demande de matériel a été transmise à l'équipe technique.",
    );
  } else {
    lines.push(
      "Votre demande de lecteur(s) biométrique a été transmise à l'équipe technique ITaaS.",
      `Quantité prévue : ${terminalCount} lecteur${terminalCount > 1 ? "s" : ""}.`,
    );
  }

  lines.push(
    `Pour suivre la livraison ou augmenter la quantité, appelez-nous au ${phone}.`,
  );

  return lines.join(" ");
}

export function buildBiometricLeadNote(opts: {
  industrySlug: string | null;
  terminalCount: number;
  classroomCount?: number;
}): string {
  const parts = [`Lecteurs biométriques demandés : ${opts.terminalCount}`];
  if (opts.classroomCount && opts.classroomCount > 0) {
    parts.push(`Salles de classe : ${opts.classroomCount}`);
  }
  if (opts.industrySlug) {
    parts.push(`Secteur : ${opts.industrySlug}`);
  }
  return parts.join(" — ");
}
